import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError, retry, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  OrderConfig,
  OrderDetails,
  CartSummary,
  ProductItem,
  SchemeRow,
  OrderPreviewItem,
  GrandTotal,
  ProductFilter,
  SchemeItemContext,
  ProductTagItem,
  ProductTagMasterModel
} from './order-workflow.models';

@Injectable({
  providedIn: 'root'
})
export class OrderWorkflowService {
  private baseUrl = environment.apiBaseUrl;
  private apiversion = environment.apiversion;

  private buildUrl(path: string): string {
    const base = this.baseUrl.replace(/\/+$/g, '');
    let normalizedPath = path.replace(/^\/+/, '');
    // remove leading 'api/' to avoid double '/api/api' when caller passes 'api/...' paths
    normalizedPath = normalizedPath.replace(/^api\/+/, '');
    const apiBase = base.endsWith('/api') ? base : `${base}/api`;
    // collapse any accidental /api/api occurrences
    return `${apiBase}/${normalizedPath}`.replace(/\/api\/api(\/|$)/gi, '/api$1');
  }
  private getForecastTransactionDate(): string {
    const now = new Date();
    const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return monthStart.toISOString();
  }
  getForecast(customerId: string): Observable<any> {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return this.http.get(this.buildUrl(`forecast/getforecast?CustomerCode=${customerId}&Month=${month}&Year=${year}&api-version=${this.apiversion}`));
  }

  // Observables for shared state
  private orderDetailsSubject = new BehaviorSubject<OrderDetails | null>(null);
  public orderDetails$ = this.orderDetailsSubject.asObservable();

  private cartSummarySubject = new BehaviorSubject<CartSummary>({
    quantity: 0,
    totalitem: 0,
    lineTotalTax: 0,
    dislineTotalTax: 0.0,
    subTotal: 0.0,
    displaySubTotal: '₹0.00',
    lineTotal: 0.0,
    dislineTotal: '₹0.00',
  });
  public cartSummary$ = this.cartSummarySubject.asObservable();

  private productsSubject = new BehaviorSubject<ProductItem[]>([]);
  public products$ = this.productsSubject.asObservable();

  private schemesSubject = new BehaviorSubject<SchemeRow[]>([]);
  public schemes$ = this.schemesSubject.asObservable();

  private previewItemsSubject = new BehaviorSubject<OrderPreviewItem[]>([]);
  public previewItems$ = this.previewItemsSubject.asObservable();

  private grandTotalSubject = new BehaviorSubject<GrandTotal | null>(null);
  public grandTotal$ = this.grandTotalSubject.asObservable();

  constructor(private http: HttpClient) { }

  // State management methods
  setOrderDetails(details: OrderDetails): void {
    this.orderDetailsSubject.next(details);
  }

  getOrderDetails(): OrderDetails | null {
    return this.orderDetailsSubject.value;
  }

  setCartSummary(summary: CartSummary): void {
    this.cartSummarySubject.next(summary);
  }

  getCartSummary(): CartSummary {
    return this.cartSummarySubject.value;
  }

  setProducts(products: ProductItem[]): void {
    this.productsSubject.next(products);
  }

  getProducts(): ProductItem[] {
    return this.productsSubject.value;
  }

  setSchemes(schemes: SchemeRow[]): void {
    this.schemesSubject.next(schemes);
  }

  getSchemes(): SchemeRow[] {
    return this.schemesSubject.value;
  }

  setPreviewItems(items: OrderPreviewItem[]): void {
    this.previewItemsSubject.next(items);
  }

  getPreviewItems(): OrderPreviewItem[] {
    return this.previewItemsSubject.value;
  }

  setGrandTotal(total: GrandTotal | null): void {
    this.grandTotalSubject.next(total);
  }

  getGrandTotal(): GrandTotal | null {
    return this.grandTotalSubject.value;
  }

  // Reset all state
  resetWorkflow(): void {
    this.orderDetailsSubject.next(null);
    this.cartSummarySubject.next({
      quantity: 0,
      totalitem: 0,
      lineTotalTax: 0,
      dislineTotalTax: 0.0,
      subTotal: 0.0,
      displaySubTotal: '₹0.00',
      lineTotal: 0.0,
      dislineTotal: '₹0.00',
    });
    this.productsSubject.next([]);
    this.schemesSubject.next([]);
    this.previewItemsSubject.next([]);
    this.grandTotalSubject.next(null);
  }

  // API Methods

  /**
   * Fetch user companies
   */
  fetchCompanies(userName: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/userlocation/compnies`, { userName });
  }

  /**
   * Fetch divisions based on company
   */
  fetchDivisions(userName: string, companyId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/userlocation/divisions`, {
      userName,
      companyId
    });
  }

  /**
   * Fetch distribution channels
   */
  fetchDistributions(userName: string, companyId: string, divisonId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/userlocation/distributors`, {
      userName,
      companyId,
      divisonId
    });
  }

  /**
   * Fetch locations
   */
  fetchLocations(userName: string, companyId: string, divisonId: string, disributorId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/userlocation/locations`, {
      userName,
      companyId,
      divisonId,
      disributorId
    });
  }

  /**
   * Fetch user context
   */
  fetchUserContext(userName: string, companyId: string, divisonId: string, disributorId: string, locationId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/userlocation/usercontext`, {
      userName,
      companyId,
      divisonId,
      disributorId,
      locationId
    });
  }

  /**
   * Fetch user profile with locations and ship-to
   */
  fetchUserProfile(userName: string, excludeSelf: boolean = false): Observable<any> {
    return this.http.post(`${this.baseUrl}/userlocation`, { userName, excludeSelf });
  }

  /**
   * Get customer credit balance
   */
  getCustomerCreditBalance(customerCode: string): Observable<number> {
    return this.http.post<any>(`${this.baseUrl}/userprofile/getcustomercreditbalance`, {
      customerCode
    }).pipe(
      map((res: any) => {
        if (res.success && res.result) {
          return res.result;
        }
        return 0;
      })
    );
  }

  /**
   * Fetch products with optional filters
   */
  fetchProducts(
    customerCode: string,
    locationId: string,
    customerContextId: string,
    companyId: string,
    distributionChannelId: string,
    basketType: string,
    filters?: any,
    basketCustomerId?: string,
    inventoryCustomerId?: string,
    skipCartFetch?: boolean
  ): Observable<ProductItem[]> {
    const request: any = {
      pagination: {
        pageNumber: 1,
        pageSize: 5000,
      },
      customerCode,
      locationId,
      customerContextId,
      companyId,
      distributionChannelId
    };

    if (filters && Object.keys(filters).length > 0) {
      request.filters = filters;
    }

    // Add inventory filtering if provided (for Sales Invoice flow)
    if (inventoryCustomerId) {
      request.inventoryCustomerId = inventoryCustomerId;
    }

    return this.http.post<any>(
      `${this.baseUrl}/product/list?api-version=${this.apiversion}`,
      request
    ).pipe(
      switchMap((res) => {
        if (res.success && res.result) {
          const products = res.result.map((product: any) => ({
            uomConversions: (product.uomConversion || [])
              .slice()
              .sort((a: any, b: any) => (a?.priorityRank || 0) - (b?.priorityRank || 0)),
            productId: product.productId,
            productCode: product.productCode,
            name: product.productName,
            parentProduct: product.parentProduct || product.parentProductName || product.parentProductCode || '',
            code: product.productCode,
            sku: product.sku,
            group: product.productGroup,
            subGroup: product.productSubGroup,
            segment: product.segment,
            tags: product.productTags || [],
            currency: product.currency,
            qty: product.quantity,
            basePrice: product.basePrice,
            rate: product.displaySellingPrice,
            sellingPrice: product.sellingPrice ?? product.basePrice ?? 0,
            displaymrp: product.displayProductMrp,
            mrp: product.productMrp,
            uom: ((product.uomConversion || [])
              .slice()
              .sort((a: any, b: any) => (a?.priorityRank || 0) - (b?.priorityRank || 0))[0]?.fromUom) || 'PKT',
            fromUom: ((product.uomConversion || [])
              .slice()
              .sort((a: any, b: any) => (a?.priorityRank || 0) - (b?.priorityRank || 0))[0]?.fromUom) || 'PKT',
            toUom: ((product.uomConversion || [])
              .slice()
              .sort((a: any, b: any) => (a?.priorityRank || 0) - (b?.priorityRank || 0))[0]?.toUom) || 'PKT',
            baseQty: Number(((product.uomConversion || [])
              .slice()
              .sort((a: any, b: any) => (a?.priorityRank || 0) - (b?.priorityRank || 0))[0]?.baseQty) || 1),
            strikethrough: product.productStrikethroughPrice,
            weight: product.productWeight,
            thumbnail: product.thumbnailUrl,
            thumbnailUrl: product.thumbnailUrl,
            images: product.imageUrls,
            promotions: product.promotions,
            availableStock: product.availableStock,
            orderQty: product.orderQtyInPackets,
            categoryName: product.categoryName || product.productGroup || '',
            subCategoryName: product.subCategoryName || product.productSubGroup || '',
            netWeight: Number(product.netWeight || product.productWeight || 0),
            grossWeight: Number(product.grossWeight || 0),
            taxDetails: product.taxDetails || product.taxes || null
          }));

          // Skip cart fetch for SalesInvoice initial load (basket is empty after clear)
          if (skipCartFetch) {
            return of(products.map((p: any) => ({
              ...p,
              extra: null,
              isInCart: false,
              cartQuantity: 0,
              quantity: null,
            })));
          }

          // Merge with cart data - use basketCustomerId if provided, otherwise use customerCode
          const cartCustomerId = basketCustomerId || customerCode;
          return this.getCart(cartCustomerId, basketType).pipe(
            map((cartRes: any) => {
              if (cartRes.success && cartRes.result) {
                const cartItems = cartRes.result.items || [];
                const mergedProducts = products.map((p: any) => {
                  const cartItem = cartItems.find(
                    (e: any) => String(e.productId) === String(p.productId)
                  );
                  return {
                    ...p,
                    extra: cartItem || null,
                    isInCart: !!cartItem,
                    cartQuantity: cartItem ? cartItem.quantity || 0 : 0,
                    quantity: cartItem ? cartItem.quantity || null : null,
                    lineTotal: cartItem?.lineTotal,
                    displayLineTotal: cartItem?.displayLineTotal
                  };
                });

                // Sort: cart items first
                return [
                  ...mergedProducts.filter((p: any) => p.isInCart),
                  ...mergedProducts.filter((p: any) => !p.isInCart),
                ];
              }
              return products.map((p: any) => ({
                ...p,
                extra: null,
                isInCart: false,
                cartQuantity: 0,
                quantity: null,
              }));
            }),
            catchError(() => {
              return of(products.map((p: any) => ({
                ...p,
                extra: null,
                isInCart: false,
                cartQuantity: 0,
                quantity: null,
              })));
            })
          );
        }
        return of([]);
      })
    );
  }

  /**
   * Get cart for customer
   */
  getCart(customerId: string, basketType: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/baskets/${customerId}?api-version=${this.apiversion}&basketType=${basketType}`
    );
  }

  /**
   * Add or update item in cart
   */
  addToCart(
    customerId: string,
    locationId: string,
    productId: string,
    quantity: number,
    customerContextId: string,
    companyId: string,
    distributionChannelId: string,
    supplierCustomerId: string,
    basketType: string,
    discountType?: string,
    discountValue?: number,
    uom?: string
  ): Observable<any> {
    const requestPayload = {
      customerId,
      locationId,
      productId,
      quantity,
      customerContextId,
      companyId,
      distributionChannelId,
      supplierCustomerId,
      basketType,
      discountType: discountType || 'None',
      discountValue: discountValue || 0,
      uom: uom || 'PKT'
    };

    return this.http.post(
      `${this.baseUrl}/baskets/items?api-version=${this.apiversion}`,
      requestPayload
    );
  }

  /**
   * Add multiple items to cart in bulk (avoids race conditions)
   */
  addToCartBulk(
    customerId: string,
    customerContextId: string,
    companyId: string,
    distributionChannelId: string,
    locationId: string,
    items: { productId: number; quantity: number }[],
    supplierCustomerId: string,
    basketType: string,
    sourceSalesOrderId?: string
  ): Observable<any> {
    const requestPayload: any = {
      customerId,
      customerContextId,
      companyId: parseInt(companyId, 10) || 0,
      distributionChannelId: parseInt(distributionChannelId, 10) || 0,
      locationId: parseInt(locationId, 10) || 0,
      items,
      supplierCustomerId,
      basketType
    };

    if (sourceSalesOrderId) {
      requestPayload.sourceSalesOrderId = sourceSalesOrderId;
    }

    return this.http.post(
      `${this.baseUrl}/baskets/items/bulk?api-version=${this.apiversion}`,
      requestPayload
    );
  }

  /**
   * Remove item from cart
   */
  removeFromCart(customerId: string, productId: string, basketType: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/baskets/${customerId}/items/${productId}?api-version=${this.apiversion}&basketType=${basketType}`
    );
  }

  /**
   * Clear entire cart
   */
  clearCart(customerId: string, basketType: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/baskets/${customerId}/items/?api-version=${this.apiversion}&basketType=${basketType}`
    );
  }

  /**
   * Delete basket
   */
  deleteBasket(customerId: string, basketType: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/baskets/${customerId}?api-version=${this.apiversion}&basketType=${basketType}`
    );
  }

  /**
   * Conditionally delete basket based on source context.
   * In SO→PO mode (salesOrderId provided): deletes only if basket was created from a different SO.
   * In Direct PO mode (no salesOrderId): deletes only if basket was created from an SO conversion.
   */
  smartClearBasket(customerId: string, basketType: string, salesOrderId?: string): Observable<any> {
    const soParam = salesOrderId ? `&salesOrderId=${salesOrderId}` : '';
    return this.http.delete(
      `${this.baseUrl}/baskets/${customerId}/smart-clear?api-version=${this.apiversion}&basketType=${basketType}${soParam}`
    );
  }

  /**
   * Fetch product filters
   */
  fetchProductFilters(
    customerCode: string,
    locationId: string,
    customerContextId: string,
    companyId: string,
    distributionChannelId: string,
    filters?: any,
    inventoryCustomerId?: string
  ): Observable<any> {
    const filterRequest: any = {
      filters: filters || {},
      customerCode,
      locationId,
      customerContextId,
      companyId,
      distributionChannelId
    };

    if (inventoryCustomerId) {
      filterRequest.inventoryCustomerId = inventoryCustomerId;
    }

    return this.http.post(
      `${this.baseUrl}/product/filter?api-version=${this.apiversion}`,
      filterRequest
    );
  }

  /**
   * Fetch group schemes
   */
  fetchGroupSchemes(
    customerCode: string,
    customerContextId: string,
    locationId: string,
    selectedProducts: any[]
  ): Observable<any> {
    const schemePayload = {
      schemeRequest: {
        customerCode,
        customerContextId,
        locationId,
        items: selectedProducts
          .filter((row: any) => row.qty > 0)
          .map((row: any) => ({
            productId: parseInt(row.productId, 10),
            qty: row.qty || 0,
            lineTotal: row.lineTotal || 0,
          })),
      },
    };

    return this.http.post(
      `${this.baseUrl}/scheme/group?api-version=${this.apiversion}`,
      schemePayload
    );
  }

  /**
   * Add free item to cart
   */
  addFreeItemToCart(
    customerId: string,
    locationId: string,
    customerContextId: string,
    productId: number,
    parentProductId: number,
    quantity: number,
    basketType: string,
    schemeContext: SchemeItemContext
  ): Observable<any> {
    const requestPayload = {
      customerId,
      locationId,
      customerContextId,
      productId,
      parentProductId,
      quantity,
      basketType,
      schemeContext
    };

    return this.http.post(
      `${this.baseUrl}/baskets/addfreeitem?api-version=${this.apiversion}`,
      requestPayload
    );
  }

  /**
   * Remove free item from cart
   */
  removeFreeItemFromCart(
    customerId: string,
    productId: string,
    schemeProductId: string,
    basketType?: string
  ): Observable<any> {
    const basketParam = basketType ? `&basketType=${basketType}` : '';
    return this.http.delete(
      `${this.baseUrl}/baskets/${customerId}/freeItems/${productId}/${schemeProductId}?api-version=${this.apiversion}${basketParam}`
    );
  }

  /**
   * Get order preview
   */
  getOrderPreview(customerId: string, basketType: string): Observable<any> {
    const url = `${this.baseUrl}/baskets/orderpreview/${customerId}?api-version=${this.apiversion}&basketType=${basketType}`;
    return this.http.get(url);
  }

  /**
   * Submit primary order
   */
  submitPrimaryOrder(
    customerCode: string,
    remarks: string,
    supplierCustomerId?: string | null,
    companyId?: string,
    divisionId?: string,
    distributionChannelId?: string,
    locationId?: string,
    salesOrderId?: string | null
  ): Observable<any> {
    const customerRequest: any = {
      customerId: customerCode,  // Backend expects 'customerId', not 'customerCode'
      remarks
    };

    // Include supplierCustomerId if provided (for supplier orders)
    if (supplierCustomerId) {
      customerRequest.supplierCustomerId = supplierCustomerId;
    }

    // Include context fields if provided
    if (companyId) {
      customerRequest.companyId = companyId;
    }
    if (divisionId) {
      customerRequest.divisionId = divisionId;
    }
    if (distributionChannelId) {
      customerRequest.distributionChannelId = distributionChannelId;
    }
    if (locationId) {
      customerRequest.locationId = locationId;
    }
    // Include salesOrderId if converting from SO
    if (salesOrderId) {
      customerRequest.salesOrderId = salesOrderId;
    }

    console.log('[submitPrimaryOrder] Sending request:', customerRequest);

    return this.http.post(
      `${this.baseUrl}/order/orders`,
      customerRequest
    );
  }

  /**
   * Update ordered CB count in forecast for Primary Order items.
   */
  updateForecastOrderedCB(
    customerCode: string,
    items: { productId: number; orderedCB: number }[]
  ): Observable<any> {
    const transactionDate = this.getForecastTransactionDate();
    const payload = {
      customerCode,
      CustomerCode: customerCode,
      forecastItems: items.map((item) => ({
        productId: item.productId,
        orderedCB: item.orderedCB,
        uomId: 1,
        transactionDate,
        isActive: true,
        createdBy: 0,
        updatedBy: 0,
        l3MAvgCB: 0,
        lmcb: 0,
        lmForecastAccuracyPer: 0,
        currentStock: 0,
        productTag: ''
      }))
    };

    const url = this.buildUrl(`forecast/updateforecastorderedcb?api-version=${this.apiversion}`);
    console.log('[updateForecastOrderedCB] Sending request:', payload);
    console.log('[updateForecastOrderedCB] URL:', url);

    return this.http.post(url, payload).pipe(
      tap((res) => console.log('[updateForecastOrderedCB] Response:', res)),
      retry(1),
      catchError((err) => {
        console.error('[updateForecastOrderedCB] Error after retry:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Submit sales order
   */
  submitSalesOrder(
    supplierId: string,
    customerId: string,
    locationId: string,
    customerContextId: string,
    remarks: string,
    companyId?: string,
    divisionId?: string,
    distributionChannelId?: string
  ): Observable<any> {
    const customerRequest: any = {
      supplierId,
      customerId,
      locationId,
      customerContextId,
      remarks
    };

    // Include context fields if provided
    if (companyId) {
      customerRequest.companyId = companyId;
    }
    if (divisionId) {
      customerRequest.divisionId = divisionId;
    }
    if (distributionChannelId) {
      customerRequest.distributionChannelId = distributionChannelId;
    }

    return this.http.post(
      `${this.baseUrl}/salesorders/direct`,
      customerRequest
    );
  }

  /**
   * Get user profile (for credit balance check)
   */
  getUserProfile(userName: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/userprofile`, { userName });
  }

  /**
   * Get sales order items for conversion to invoice
   */
  getSalesOrderItems(salesOrderId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/salesorders/${salesOrderId}/items?api-version=${this.apiversion}`
    );
  }

  /**
   * Submit sales invoice from Sales Order
   * POST /api/salesinvoice
   * @param salesOrderId - Original SO ID
   * @param customerId - Customer ID
   * @param remarks - Optional remarks
   */
  submitSalesInvoiceFromSO(
    salesOrderId: string,
    customerId: string,
    remarks: string,
    orderLevelDiscountType: string = 'None',
    orderLevelDiscountValue: number = 0
  ): Observable<any> {
    const payload = {
      salesOrderId,
      customerId,
      remarks,
      orderLevelDiscountType,
      orderLevelDiscountValue
    };

    console.log('[submitSalesInvoiceFromSO] Sending request:', payload);

    return this.http.post(
      `${this.baseUrl}/salesinvoice`,
      payload
    );
  }

  /**
   * Submit direct sales invoice (without Sales Order)
   * POST /api/salesinvoice/direct
   * @param supplierId - Supplier (logged-in user) customer code
   * @param customerId - Customer ID
   * @param customerContextId - Customer context ID
   * @param locationId - Location ID
   * @param remarks - Optional remarks
   */
  submitDirectSalesInvoice(
    supplierId: string,
    customerId: string,
    customerContextId: string,
    locationId: number,
    remarks: string,
    orderLevelDiscountType: string = 'None',
    orderLevelDiscountValue: number = 0
  ): Observable<any> {
    const payload = {
      supplierId,
      customerId,
      customerContextId,
      locationId,
      remarks,
      orderLevelDiscountType,
      orderLevelDiscountValue
    };

    console.log('[submitDirectSalesInvoice] Sending request:', payload);

    return this.http.post(
      `${this.baseUrl}/salesinvoice/direct`,
      payload
    );
  }

  /**
   * Get real-time inventory for customer
   */
  getInventory(customerCode: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/closingstock/inventorylist?CustomerCode=${customerCode}&api-version=${this.apiversion}`
    );
  }

  /**
   * Get inventory for specific products only (for validation)
   */
  getInventoryByProducts(customerCode: string, productCodes: string[]): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/closingstock/inventorybyproducts?api-version=${this.apiversion}`,
      { customerCode, productCodes }
    );
  }

  /**
   * Validate inventory for Sales Invoice items
   * Checks if order quantity exceeds available stock
   * @param soItems - Sales Order items with productId and quantity
   * @param products - Products with availableStock from product/list API
   * @returns Object with validItems (can be added to cart) and insufficientItems (skipped)
   */
  validateInventory(
    soItems: any[],
    products: any[]
  ): { validItems: any[]; insufficientItems: any[] } {
    const validItems: any[] = [];
    const insufficientItems: any[] = [];

    soItems.forEach((soItem: any) => {
      // Match by productCode (string) for reliability, fallback to productId
      const matchingProduct = products.find(
        (p: any) => String(p.productCode) === String(soItem.productCode) ||
          String(p.productId) === String(soItem.productId)
      );

      // Handle both field names: orderQtyInPackets (from SO API) or orderQty (from cart)
      const orderQty = soItem.orderQtyInPackets || soItem.orderQty || soItem.quantity || 0;

      if (!matchingProduct) {
        // Product not in inventory at all
        insufficientItems.push({
          productCode: soItem.productCode || 'N/A',
          productName: soItem.productName || 'Unknown Product',
          orderQty: orderQty,
          availableStock: 0
        });
      } else if (orderQty > (matchingProduct.availableStock || 0)) {
        // Insufficient stock
        insufficientItems.push({
          productCode: matchingProduct.productCode || soItem.productCode,
          productName: matchingProduct.name || soItem.productName,
          orderQty: orderQty,
          availableStock: matchingProduct.availableStock || 0
        });
      } else {
        // Valid item - sufficient stock
        validItems.push({
          ...soItem,
          availableStock: matchingProduct.availableStock
        });
      }
    });

    return { validItems, insufficientItems };
  }

  /**
   * Set order-level discount for the entire cart
   */
  setOrderLevelDiscount(
    customerId: string,
    customerContextId: string,
    basketType: string,
    discountType: string,
    discountValue: number
  ): Observable<any> {
    const url = `${this.baseUrl}/baskets/orderleveldiscount?api-version=${this.apiversion}`;
    const body = {
      customerId,
      customerContextId,
      basketType,
      discountType,
      discountValue
    };
    return this.http.post(url, body);
  }

  applySchemeDiscount(
    customerContextId: string,
    locationId: number,
    items: {
      productId: number;
      qty: number;
      price: number;
      sellingPrice: number;
      lineTotal: number;
    }[]
  ): Observable<any> {
    const url = `${this.baseUrl}/scheme/discount?api-version=${this.apiversion}`;
    const body = {
      discountSchemeRequest: {
        customerContextId,
        locationId,
        items
      }
    };
    return this.http.post(url, body);
  }

  /**
   * Fetch Product Tag Masters from Admin API
   */
  fetchProductTagMasters(): Observable<ProductTagMasterModel[]> {
    return of([]);
  }

  /**
   * Fetch submitted purchase receipts for customer
   */
  fetchSubmittedPurchaseReceipts(customerCode: string): Observable<any[]> {
    const payload = {
      customerCode,
      receiptStatus: 1, // submitted
      pagination: {
        pageNumber: 1,
        pageSize: 500
      }
    };
    return this.http.post<any>(`${this.baseUrl}/purchasereceipt?api-version=${this.apiversion}`, payload).pipe(
      map((res: any) => {
        if (res && res.result && Array.isArray(res.result.purchaseReceipts)) {
          return res.result.purchaseReceipts;
        }
        if (res && Array.isArray(res.purchaseReceipts)) {
          return res.purchaseReceipts;
        }
        if (Array.isArray(res?.result)) {
          return res.result;
        }
        return [];
      }),
      catchError((err) => {
        console.error('Error fetching purchase receipts:', err);
        return of([]);
      })
    );
  }

  /**
   * Validate product tags before Primary Order Continue via backend API.
   * Compares active & compulsory tags in ProductTagMaster that exist in the loaded product list.
   * - Order tags: checked against current cart products.
   * - MTD tags: checked against current month submitted purchase receipts.
   */
  validatePrimaryOrderProductTags(
    customerCode: string,
    customerContextId?: string,
    companyId?: any,
    distributionChannelId?: any,
    locationId?: any,
    inventoryCustomerId?: string,
    basketType?: string
  ): Observable<{
    isValid: boolean;
    missingTags: ProductTagMasterModel[];
    validationItems?: any[];
    message?: string;
  }> {
    const payload: any = {
      customerCode
    };

    if (customerContextId) {
      payload.customerContextId = customerContextId;
    }
    if (companyId !== undefined && companyId !== null && companyId !== '') {
      payload.companyId = Number(companyId);
    }
    if (distributionChannelId !== undefined && distributionChannelId !== null && distributionChannelId !== '') {
      payload.distributionChannelId = Number(distributionChannelId);
    }
    if (locationId !== undefined && locationId !== null && locationId !== '') {
      payload.locationId = Number(locationId);
    }
    if (inventoryCustomerId) {
      payload.inventoryCustomerId = inventoryCustomerId;
    }
    if (basketType) {
      payload.basketType = basketType;
    }

    return this.http.post<any>(
      `${this.baseUrl}/product/validate-tags?api-version=${this.apiversion}`,
      payload
    ).pipe(
      map((res: any) => {
        if (res && res.result) {
          return {
            isValid: !!res.result.isValid,
            message: res.result.message || '',
            missingTags: (res.result.missingTags || []).map((t: any): ProductTagMasterModel => ({
              tagId: Number(t.tagId ?? t.TagId ?? 0),
              tagCode: t.tagCode || t.TagCode || '',
              tagName: t.tagName || t.TagName || t.tagCode || t.TagCode || '',
              type: t.type ?? t.Type ?? t.validationType ?? t.ValidationType ?? null,
              color: t.color || t.Color || '#6c757d',
              isCompulsory: t.isCompulsory !== undefined
                ? (t.isCompulsory === true || String(t.isCompulsory).toLowerCase() === 'true')
                : (t.IsCompulsory !== undefined
                    ? (t.IsCompulsory === true || String(t.IsCompulsory).toLowerCase() === 'true')
                    : false),
              isActive: t.isActive !== undefined
                ? (t.isActive === true || String(t.isActive).toLowerCase() === 'true')
                : (t.IsActive !== undefined
                    ? (t.IsActive === true || String(t.IsActive).toLowerCase() === 'true')
                    : true)
            })),
            validationItems: (res.result.validationItems || res.result.items || []).map((i: any) => ({
              productId: String(i.productId || i.ProductId || ''),
              productCode: i.productCode || i.ProductCode || '',
              productName: i.productName || i.ProductName || '',
              parentProduct: i.parentProduct || i.ParentProduct || '',
              tags: (i.tags || i.productTags || i.Tags || i.ProductTags || []).map((t: any): ProductTagItem => ({
                tagId: typeof t === 'object' ? (t.tagId !== undefined ? Number(t.tagId) : (t.TagId !== undefined ? Number(t.TagId) : undefined)) : undefined,
                tagCode: typeof t === 'string' ? t : (t.tagCode || t.TagCode || t.code || ''),
                tagName: typeof t === 'string' ? t : (t.tagName || t.TagName || t.name || t.tagCode || t.TagCode || ''),
                color: typeof t === 'object' ? (t.color || t.Color || '#6c757d') : '#6c757d',
                isCompulsory: typeof t === 'object'
                  ? (t.isCompulsory === true || t.IsCompulsory === true || String(t.isCompulsory).toLowerCase() === 'true' || String(t.IsCompulsory).toLowerCase() === 'true')
                  : false,
                type: typeof t === 'object' ? (t.type || t.Type || 'Order') : 'Order',
                isActive: typeof t === 'object'
                  ? (t.isActive !== undefined ? (t.isActive === true || String(t.isActive).toLowerCase() === 'true') : (t.IsActive !== undefined ? (t.IsActive === true || String(t.IsActive).toLowerCase() === 'true') : true))
                  : true
              })),
              type: i.type || i.validationType || i.Type || '',
              isCompulsory: i.isCompulsory !== undefined
                ? (i.isCompulsory === true || String(i.isCompulsory).toLowerCase() === 'true')
                : (i.IsCompulsory !== undefined
                    ? (i.IsCompulsory === true || String(i.IsCompulsory).toLowerCase() === 'true')
                    : false)
            }))
          };
        }
        return { isValid: true, missingTags: [], validationItems: [], message: '' };
      }),
      catchError((err) => {
        console.error('Error during product tag validation API call:', err);
        return of({ isValid: true, missingTags: [], validationItems: [], message: '' });
      })
    );
  }

  validateProductTags(
    customerCode: string,
    customerContextId?: string,
    companyId?: any,
    distributionChannelId?: any,
    locationId?: any,
    inventoryCustomerId?: string,
    basketType?: string
  ): Observable<{
    isValid: boolean;
    missingTags: ProductTagMasterModel[];
    validationItems?: any[];
    message?: string;
  }> {
    return this.validatePrimaryOrderProductTags(
      customerCode,
      customerContextId,
      companyId,
      distributionChannelId,
      locationId,
      inventoryCustomerId,
      basketType
    );
  }
}
